import { BarChart3, Newspaper, TrendingUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MarketIntelligenceWidget } from "@/components/market-intelligence/MarketIntelligenceWidget";
import { NewsEditorialCard } from "@/components/NewsEditorialCard";
import { useSiteBranding } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { useStructuredData } from "@/hooks/useStructuredData";
import { api } from "@/lib/api";

export const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const { buildBrandedPath } = useSiteBranding();

  useSeo({
    title: "Market Insights | SG Prop Portal",
    description: "Read premium Singapore property market insights shaped by the latest URA and HDB releases.",
  });

  const loadNews = useCallback(async () => {
    try {
      const response = await api.get("/insights");
      setArticles(response.data);
    } catch {
      setArticles([]);
    }
  }, []);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const featuredArticle = articles[0];
  const editorialCards = articles.slice(1);
  const collectionSchema = useMemo(
    () =>
      articles.length
        ? {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "SG Prop Portal Market Insights",
            description: "Premium Singapore property market insights shaped by the latest URA and HDB releases.",
            url: "https://sgpropportal.com/insights",
            mainEntity: {
              "@type": "ItemList",
              itemListElement: articles.map((article, index) => ({
                "@type": "ListItem",
                position: index + 1,
                url: `https://sgpropportal.com/insights/${article.slug || article.id}`,
                name: article.title,
              })),
            },
          }
        : null,
    [articles],
  );

  useStructuredData("news-collection-schema", collectionSchema);
  const insightItems = useMemo(
    () => [
      {
        label: "Editorials live",
        value: `${articles.length}`,
        detail: "Fresh market reads framed for decision-making, not just headlines.",
        icon: Newspaper,
      },
      {
        label: "Strongest theme",
        value: featuredArticle?.market_focus || "Market positioning",
        detail: featuredArticle?.key_stat || "Track where buyer demand, financing, and pricing pressure are converging.",
        icon: TrendingUp,
      },
      {
        label: "Reading angle",
        value: "Actionable",
        detail: "Every piece closes with a clear next-step advisory from the SG Prop Portal team’s lens.",
        icon: BarChart3,
      },
    ],
    [articles, featuredArticle],
  );

  return (
    <div>
      <section className="section-shell">
        <div className="max-w-4xl">
          <p className="subtle-label">Market Insights</p>
          <h1 className="mt-3 text-4xl font-semibold text-[#001935] md:text-6xl" data-testid="news-page-heading">
            Official market signals, translated into clearer property decisions.
          </h1>
          <p className="mt-4 text-base leading-8 text-[#31455f]">
            SG Prop Portal’s insights hub turns the latest URA and HDB announcements into premium, investor-aware briefings with district context and live inventory relevance.
          </p>
        </div>
      </section>

      <section className="section-shell pt-2" data-testid="news-market-intelligence-section">
        <MarketIntelligenceWidget
          subheading="Keep the editorial market reads below, but search a live district transaction layer here for caveat-backed PSF, recent prices, and trend context."
          testIdPrefix="news-market-intelligence"
          title="Search district transaction intelligence before you read the headlines."
        />
      </section>

      <section className="section-shell pt-4">
        {!featuredArticle ? (
          <div className="rounded-[34px] border border-[#001935]/10 bg-white/85 p-8 shadow-[0_24px_80px_rgba(0,25,53,0.08)]" data-testid="news-page-empty-state">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]">Editorial pipeline warming up</p>
            <h2 className="mt-3 text-3xl font-semibold text-[#001935]">Fresh insights are being curated in the admin CMS.</h2>
            <p className="mt-4 max-w-3xl text-sm leading-8 text-[#42566f]">
              The first published market insights will appear here once our editorial team reviews and releases the new URA and HDB drafts.
            </p>
          </div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <NewsEditorialCard article={featuredArticle} basePath="/insights" featured />
            </div>

            <aside className="lg:col-span-4">
              <div className="rounded-[34px] border border-[#001935]/10 bg-[#001935] p-6 text-white shadow-[0_24px_80px_rgba(0,25,53,0.16)] md:p-8" data-testid="news-market-insights-panel">
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#F5D98C]">Market Insights</p>
                <h2 className="mt-4 text-3xl font-semibold text-white">What matters in today’s official releases.</h2>
                <div className="mt-8 space-y-5">
                  {insightItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div className="rounded-[24px] border border-white/10 bg-white/5 p-4" data-testid={`news-insight-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`} key={item.label}>
                        <div className="flex items-center gap-3 text-[#F5D98C]"><Icon className="h-5 w-5" /><p className="text-xs font-bold uppercase tracking-[0.18em]">{item.label}</p></div>
                        <p className="mt-4 text-2xl font-semibold leading-tight text-white">{item.value}</p>
                        <p className="mt-3 text-sm leading-7 text-white/72">{item.detail}</p>
                      </div>
                    );
                  })}
                </div>
                <Link className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#F5D98C]" data-testid="news-market-insights-contact-link" to={buildBrandedPath("/contact")}>
                  Talk through your shortlist
                </Link>
              </div>
            </aside>
          </div>
        )}
      </section>

      <section className="section-shell pt-2">
        <div className="grid gap-6 lg:grid-cols-2">
          {editorialCards.map((article, index) => (
            <NewsEditorialCard article={article} basePath="/insights" index={index} key={article.id} />
          ))}
        </div>
      </section>
    </div>
  );
};